import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCategories } from '../../services/api';
import { Home, Search, ChevronRight, Grid, List, X } from 'lucide-react';
import Loading from '../../components/common/Loading';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import MobileBottomNav from '../../components/layout/MobileBottomNav';

const CategoriesListingPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter(category =>
        category.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [searchQuery, categories]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const categoriesData = await getCategories();
      
      const categories = categoriesData.data || categoriesData || [];
      
      const mainCategories = categories.filter(cat => cat.parent_id === null);
      setCategories(mainCategories);
      setFilteredCategories(mainCategories);
      
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
      setCategories([]);
      setFilteredCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    navigate(`/category/${category.id}`, { state: { category } });
  };

  const getCategoryIcon = (category) => {
    if (!category?.icon_name) return '📦';
    
    const iconMap = {
      'hardware-chip': '💻',
      'phone-portrait': '📱',
      'car': '🚗',
      'shirt': '👕',
      'sparkles': '✨',
      'heart': '❤️',
      'restaurant': '🍴',
      'business': '🏢',
      'home': '🏠',
      'paw': '🐾',
      'construct': '🔧',
      'laptop': '💻',
      'tv': '📺',
      'camera': '📷',
      'game-controller': '🎮',
      'hardware-chip-outline': '💻',
      'print': '🖨️',
      'flash': '⚡',
      'tablet-portrait': '📱',
      'ear': '🎧',
      'watch': '⌚',
      'headset': '🎧',
      'car-sport': '🏎️',
      'bicycle': '🚲',
      'bus': '🚌',
      'build': '🔧',
      'disc': '💿',
      'boat': '🚤',
      'man': '👨',
      'woman': '👩',
      'footsteps': '👟',
      'bag': '👜',
      'diamond': '💎',
      'shirt-outline': '👔',
      'glasses': '👓',
      'basketball': '🏀',
      'water': '💧',
      'cut': '✂️',
      'map': '🗺️',
    };
    
    return iconMap[category.icon_name] || '📦';
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f5f5]">
        <Header />
        <div className="px-4 py-6">
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#fff5e6] rounded-full flex items-center justify-center">
              <Grid className="w-8 h-8 text-[#ff660e]" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Categories</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#ff660e] text-white font-medium rounded-lg hover:bg-[#e55a0d] transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <Header />

      {/* Main Content */}
      <div className="px-4 py-4">
        {/* Back Button and Title */}
        <div className="mb-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center text-sm text-gray-600 hover:text-[#ff660e] mb-3"
          >
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
            Back
          </button>
          
          <h1 className="text-2xl font-bold text-gray-900">All Categories</h1>
          <p className="text-gray-600 mt-1">
            Browse {categories.length} categories
          </p>
        </div>
        
        {/* Search Bar - Mobile Optimized */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search categories..."
            className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent bg-white"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {/* View Toggle */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            {filteredCategories.length} categories found
          </div>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-[#ff660e] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 ${viewMode === 'list' ? 'bg-[#ff660e] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {/* Categories Grid/List */}
        {loading ? (
          <div className="py-12">
            <Loading text="Loading categories..." size="large" color="#ff660e" />
          </div>
        ) : filteredCategories.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-2 gap-3">
              {filteredCategories.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category)}
                  className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-100 hover:border-[#ff660e] text-left"
                >
                  <div className="p-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-3"
                      style={{ 
                        backgroundColor: category.background_color || '#ff660e',
                        color: category.icon_color || '#ffffff'
                      }}
                    >
                      {getCategoryIcon(category)}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-[#ff660e] transition-colors line-clamp-2">
                      {category.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        Browse
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#ff660e] transition-colors" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              {filteredCategories.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category)}
                  className="w-full group flex items-center p-3 hover:bg-[#fff5e6] transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-xl mr-3"
                    style={{ 
                      backgroundColor: category.background_color || '#ff660e',
                      color: category.icon_color || '#ffffff'
                    }}
                  >
                    {getCategoryIcon(category)}
                  </div>
                  <div className="flex-grow text-left">
                    <h3 className="font-semibold text-gray-900 group-hover:text-[#ff660e] transition-colors text-sm">
                      {category.name}
                    </h3>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#ff660e] transition-colors" />
                </button>
              ))}
            </div>
          )
        ) : (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No categories found</h3>
            <p className="text-gray-600 mb-6 px-4">
              No categories match "{searchQuery}". Try a different search term.
            </p>
            <div className="flex flex-col gap-3 px-4">
              <button
                onClick={() => setSearchQuery('')}
                className="w-full py-3 bg-[#ff660e] text-white font-medium rounded-lg hover:bg-[#e55a0d] transition-colors"
              >
                Clear Search
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors border border-gray-200"
              >
                Back to Home
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default CategoriesListingPage;